import React from 'react'
import { MdOutlineFlag, MdOutlinePlayArrow } from 'react-icons/md'
import { TbCopyPlus } from 'react-icons/tb'

import { IResourceConfig } from '@/Core/Contracts/UI'
import { DEFAULT_DETAIL_WIDTH } from '@/Ship/Components/UI/AppDrawer/AppDrawer'
import { statusFieldView } from '@/Ship/Components/UI/StatusBadge/statusField'
import { MAX_ODOMETER } from '@/Ship/Helpers/odometer'
import { MILEAGE_DEVIATION_OPTIONS } from '../Contracts/MileageDeviation'
import { SHIFT_STATUS_OPTIONS } from '../Contracts/ShiftStatus'
import { formatDuration, formatKm, formatKmDelta } from '../Helpers/shiftFormat'
import { ShiftViewActions } from '../Hooks/ShiftViewActions'
import { ShiftFormTemplate } from '../Templates/ShiftFormTemplate'
import { ShiftViewTemplate } from '../Templates/ShiftViewTemplate'

/** Водії у формі — лише активні; у фільтрі — будь-якого статусу, крім видаленого */
const DRIVER_ROLE_FILTER = { filterRoles: { op: 'like', value: 'ROLE_DRIVER' } }

const userLabel = (item: any) => item?.fullName || `${item?.lastName || ''} ${item?.firstName || ''}`.trim() || '—'

/** Колонки з одиницями виміру: значення форматуються, а не показуються сирим числом */
const KM_COLUMNS = ['startOdometer', 'endOdometer', 'mileage', 'wialonMileage']

/**
 * Зміни водіїв: журнал із фільтрами, картка з діями «Розпочати» / «Завершити»,
 * форма створення й правки (диспетчер). Статус змінюється лише діями.
 */
export const ShiftResource: IResourceConfig = {
    title: 'Зміни',
    restUrl: '/services/shifts',
    pageUrl: '/shifts',
    fields: {
        id: {
            label: 'ID',
            type: 'number',
            inputProps: { disabled: true },
            isFilterRange: true,
        },
        code: {
            label: 'Код',
            type: 'string',
            /** Порожній при створенні — бекенд згенерує (SH-ррммдд-XXXXX) */
            inputProps: { placeholder: 'Згенерується автоматично' },
        },
        shiftDate: {
            label: 'Дата зміни',
            type: 'date',
            isRequired: true,
        },
        status: {
            label: 'Статус',
            type: 'status',
            options: SHIFT_STATUS_OPTIONS,
            isFilterMultiple: true,
            /** Бейдж без меню зміни: статус змінюють лише дії «Розпочати» / «Завершити» */
            inputProps: { disabled: true },
            view: statusFieldView,
        },
        driver: {
            label: 'Водій',
            type: 'select',
            resource: 'Setting.User',
            isRequired: true,
            inputProps: {
                filter: { ...DRIVER_ROLE_FILTER, status: 'active' },
                responseLabel: userLabel,
            },
            isFilterMultiple: true,
            filterOptionsFilter: { ...DRIVER_ROLE_FILTER, status: ['invited', 'active', 'inactive'] },
        },
        vehicle: {
            label: 'Транспортний засіб',
            type: 'select',
            resource: 'Transport.Vehicle',
            isRequired: true,
            /** Фільтр за перевізником водія ставить ShiftFormTemplate */
            inputProps: { filter: { isActive: true } },
            isFilterMultiple: true,
            filterOptionsFilter: { isRemoved: false },
        },
        cluster: {
            label: 'Кластер',
            type: 'select',
            resource: 'Setting.ListCluster',
            isRequired: true,
            inputProps: { filter: { isActive: true } },
            isFilterMultiple: true,
            filterOptionsFilter: { isRemoved: false },
        },
        region: {
            label: 'Регіон',
            type: 'select',
            resource: 'Setting.ListRegion',
            isRequired: true,
            /** Регіони обраного кластера — каскад у ShiftFormTemplate */
            inputProps: { filter: { isActive: true } },
            isFilterMultiple: true,
            filterOptionsFilter: { isRemoved: false },
        },
        startedAt: {
            label: 'Дата початку зміни',
            type: 'datetime',
        },
        startOdometer: {
            label: 'Початковий одометр, км',
            type: 'number',
            inputProps: { min: 0, max: MAX_ODOMETER, precision: 0 },
        },
        documentsConfirmed: {
            label: 'Підтверджено наявність документів',
            type: 'switch',
        },
        safetyBriefingConfirmed: {
            label: 'Підтверджено проходження інструктажу з безпеки руху',
            type: 'switch',
        },
        completedAt: {
            label: 'Дата завершення зміни',
            type: 'datetime',
        },
        endOdometer: {
            label: 'Кінцевий одометр, км',
            type: 'number',
            inputProps: { min: 0, max: MAX_ODOMETER, precision: 0 },
        },
        technicalRemarks: {
            label: 'Зауваження до технічного стану ТЗ',
            type: 'textarea',
        },
        durationMinutes: {
            label: 'Загальна тривалість зміни',
            type: 'number',
            inputProps: { disabled: true },
        },
        mileage: {
            label: 'Загальний пробіг',
            type: 'number',
            inputProps: { disabled: true },
        },
        wialonMileage: {
            label: 'Пробіг за Wialon',
            type: 'number',
            /** Поки немає інтеграції з Wialon — вносить диспетчер у завершеній зміні (або ERP/API) */
            inputProps: { min: 0, max: MAX_ODOMETER, precision: 0 },
        },
        mileageDeviationStatus: {
            label: 'Контроль пробігу',
            type: 'status',
            options: MILEAGE_DEVIATION_OPTIONS,
            isFilterMultiple: true,
            /** Рахує бекенд; змінюється лише розглядом у картці */
            inputProps: { disabled: true },
            view: statusFieldView,
        },
        mileageDeviationKm: {
            label: 'Розбіжність пробігу',
            type: 'number',
            inputProps: { disabled: true },
        },
        externalCode: {
            label: 'Код ERP',
            type: 'string',
            inputProps: { disabled: true },
        },
    },
    filter: {
        includeFields: [
            'code',
            'shiftDate',
            'status',
            'driver',
            'vehicle',
            'cluster',
            'region',
            'mileageDeviationStatus',
        ],
    },
    table: {
        showColumns: [
            'code',
            'shiftDate',
            'status',
            'driver',
            'vehicle',
            'cluster',
            'region',
            'startedAt',
            'completedAt',
            'durationMinutes',
            'mileage',
            'wialonMileage',
            'mileageDeviationKm',
            'mileageDeviationStatus',
        ],
        scroll: { x: 'max-content' },
        eventHandlers: {
            onBuildColumn: {
                formatShiftColumns: ({ column }: any) => {
                    if (column.dataIndex === 'durationMinutes') {
                        column.render = (value: any) => formatDuration(value)
                    }
                    if (KM_COLUMNS.includes(column.dataIndex)) {
                        column.render = (value: any) => formatKm(value)
                    }
                    if (column.dataIndex === 'mileageDeviationKm') {
                        column.render = (value: any) => formatKmDelta(value)
                    }
                },
            },
        },
    },
    /** У перегляді «Службову інформацію» показує сам шаблон — лише у вкладці «Інформація» */
    hideServiceInfoInView: true,
    detailConfig: {
        modeConfig: {
            view: {
                template: ShiftViewTemplate,
                width: DEFAULT_DETAIL_WIDTH,
                onButtons: ({ buttons, crudContext, content }: any) => {
                    const depStore = { formContext: crudContext.formContext, crudContext }

                    buttons.startShift = {
                        label: 'Розпочати зміну',
                        icon: React.createElement(MdOutlinePlayArrow),
                        variant: 'primary',
                        size: 'middle',
                        priority: 'button',
                        sort: 1,
                        depStore,
                        depFn: ({ formContext }: any) => !!formContext?.values?._permissions?.start,
                        onClick: () => crudContext.eventManager.emit('shiftAction:openStart', { crudContext }),
                    }

                    buttons.completeShift = {
                        label: 'Завершити зміну',
                        icon: React.createElement(MdOutlineFlag),
                        variant: 'primary',
                        size: 'middle',
                        priority: 'button',
                        sort: 2,
                        depStore,
                        depFn: ({ formContext }: any) => !!formContext?.values?._permissions?.complete,
                        onClick: () => crudContext.eventManager.emit('shiftAction:openComplete', { crudContext }),
                    }

                    content.push(<ShiftViewActions key="shift-view-actions" crudContext={crudContext} />)
                },
            },
            create: { template: ShiftFormTemplate, width: DEFAULT_DETAIL_WIDTH },
            edit: { template: ShiftFormTemplate, width: DEFAULT_DETAIL_WIDTH },
        },
        mode: 'drawer',
    },
    form: { template: ShiftFormTemplate },
    headerPage: {
        eventHandlers: {
            onButtons: {
                /** Зміни на період (до 5 днів) для кількох водіїв — вікно MultiShiftDrawer (ShiftPage) */
                AddMultipleButton: ({ buttons, crudContext }: any) => {
                    buttons['AddMultipleButton'] = {
                        label: 'Додати кілька',
                        icon: React.createElement(TbCopyPlus, { size: 22 }),
                        sort: 90,
                        priority: 'button',
                        size: 'middle',
                        depFn: ({ crudContext }: any) =>
                            !!crudContext?.getStorage?.('multiShiftStore') &&
                            crudContext?.accessFn?.({ accessMode: 'create', crudContext }) === true,
                        onClick: () => {
                            crudContext?.getStorage?.('multiShiftStore')?.openDrawer()
                        },
                    }
                },
            },
        },
    },
    settings: {
        showPaginationTotal: true,
    },
}
